import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Image,
  IconButton,
  useColorModeValue,
  Select,
} from '@chakra-ui/react';
import { FaPlay } from 'react-icons/fa';
import { useSpotify } from '../context/SpotifyContext';
import { spotifyService } from '../services/spotifyService';
import { useProgress } from '../context/ProgressContext';

interface QueueViewProps {
  onClose: () => void;
}

const QueueView: React.FC<QueueViewProps> = ({ onClose }) => {
  const { accessToken } = useSpotify();
  const { setCurrentTrackId, setIsPlaying } = useProgress();
  const [queue, setQueue] = React.useState<any>(null);
  const [devices, setDevices] = React.useState<any[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = React.useState<string>('');
  const bgColor = useColorModeValue('whiteAlpha.100', 'blackAlpha.500');
  const hoverBgColor = useColorModeValue('whiteAlpha.200', 'blackAlpha.600');

  React.useEffect(() => {
    const fetchQueue = async () => {
      if (!accessToken) return;
      try {
        const queueData = await spotifyService.getQueue(accessToken);
        setQueue(queueData);
      } catch (error) {
        console.error('Error fetching queue:', error);
      }
    };

    const fetchDevices = async () => {
      if (!accessToken) return;
      try {
        const response = await fetch('https://api.spotify.com/v1/me/player/devices', {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        const data = await response.json();
        setDevices(data.devices || []);
        // Set the first active device as default, or the first device if none are active
        const activeDevice = data.devices?.find((device: any) => device.is_active) || data.devices?.[0];
        if (activeDevice) {
          setSelectedDeviceId(activeDevice.id);
        }
      } catch (error) {
        console.error('Error fetching devices:', error);
      }
    };

    fetchQueue();
    fetchDevices();
    // Refresh queue and devices every 5 seconds
    const interval = setInterval(() => {
      fetchQueue();
      fetchDevices();
    }, 5000);
    return () => clearInterval(interval);
  }, [accessToken]);

  const handlePlayTrack = async (track: any) => {
    if (!accessToken) return;
    try {
      // First, set the current track ID to start loading lyrics
      setCurrentTrackId(track.id);
      console.log('Preloading lyrics for track:', track.id);

      // Wait a moment for lyrics to start loading
      await new Promise(resolve => setTimeout(resolve, 100));

      // Prepare the play request body
      const playRequestBody: any = {
        uris: [track.uri],
      };

      // Add device_id if selected
      if (selectedDeviceId) {
        playRequestBody.device_id = selectedDeviceId;
      }

      // Then send the play request
      const playResponse = await fetch('https://api.spotify.com/v1/me/player/play', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(playRequestBody),
      });

      if (playResponse.ok) {
        setIsPlaying(true);
        onClose();
      }
    } catch (error) {
      console.error('Error playing track:', error);
    }
  };

  if (!queue) return null;

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.800"
      backdropFilter="blur(10px)"
      zIndex={10}
      p={4}
      overflowY="auto"
    >
      <VStack spacing={4} align="stretch">
        <Text fontSize="xl" fontWeight="bold" color="white">
          Up Next
        </Text>

        {devices.length > 0 && (
          <Select
            value={selectedDeviceId}
            onChange={(e) => setSelectedDeviceId(e.target.value)}
            color="white"
            bg="whiteAlpha.100"
            borderColor="whiteAlpha.300"
            _hover={{ borderColor: 'whiteAlpha.400' }}
            _focus={{ borderColor: 'whiteAlpha.500' }}
          >
            {devices.map((device) => (
              <option key={device.id} value={device.id} style={{ background: '#1a1a1a', color: 'white' }}>
                {device.name} {device.is_active ? '(Active)' : ''}
              </option>
            ))}
          </Select>
        )}
        
        {queue.queue?.map((track: any, index: number) => (
          <HStack
            key={track.id}
            p={2}
            borderRadius="md"
            bg={bgColor}
            _hover={{ bg: hoverBgColor }}
            cursor="pointer"
            onClick={() => handlePlayTrack(track)}
          >
            <Image
              src={track.album.images[0]?.url}
              alt={track.name}
              boxSize="50px"
              borderRadius="md"
              fallbackSrc="/images/album-placeholder.png"
            />
            <VStack align="start" flex={1} spacing={0}>
              <Text color="white" fontWeight="medium" noOfLines={1}>
                {track.name}
              </Text>
              <Text color="whiteAlpha.700" fontSize="sm" noOfLines={1}>
                {track.artists.map((artist: any) => artist.name).join(', ')}
              </Text>
            </VStack>
            <IconButton
              aria-label="Play track"
              icon={<FaPlay />}
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.200' }}
              onClick={(e) => {
                e.stopPropagation();
                handlePlayTrack(track);
              }}
            />
          </HStack>
        ))}
      </VStack>
    </Box>
  );
};

export default QueueView; 